import { setAccountDetail } from '@client/model/ltcFinancialPlanningModel';
import { AppState } from '@client/model/store';
import { isNullOrUndefined } from '@shared';
import { useCallback, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

type AccountDetailField = keyof LtcFinancialPlanningAccountDetail;

type AccountDetailFormErrors = Partial<Record<AccountDetailField, string>>;

interface AccountDetailFormState {
  currentValue: number | null;
  futureContributions: number | null;
  expectedReturn: number | null;
  capitalGainsTaxRate: number | null;
}

const REQUIRED_FIELDS: AccountDetailField[] = ['currentValue', 'expectedReturn', 'capitalGainsTaxRate'];

const DEFAULT_VALUES: AccountDetailFormState = {
  currentValue: null,
  futureContributions: null,
  expectedReturn: null,
  capitalGainsTaxRate: null,
};

export function useAccountDetailForm(assetType: LtcFinancialPlanningAssetType) {
  const dispatch = useDispatch();

  // Get existing detail from Redux (if any)
  const existingDetail = useSelector(
    (state: { ltcFinancialPlanning: AppState['ltcFinancialPlanning'] }) =>
      state.ltcFinancialPlanning.accountDetails[assetType],
  );

  // Convert Redux state to form state (handling 0 as null for empty display)
  const getInitialFormState = useCallback((): AccountDetailFormState => {
    if (!existingDetail) {
      return DEFAULT_VALUES;
    }
    return {
      currentValue: existingDetail.currentValue || null,
      futureContributions: existingDetail.futureContributions || null,
      expectedReturn: existingDetail.expectedReturn || null,
      capitalGainsTaxRate: existingDetail.capitalGainsTaxRate || null,
    };
  }, [existingDetail]);

  // Local form state
  const [formState, setFormState] = useState<AccountDetailFormState>(getInitialFormState);
  const [initialFormState, setInitialFormState] = useState<AccountDetailFormState>(getInitialFormState);
  const [formErrors, setFormErrors] = useState<AccountDetailFormErrors>({});
  const [formHasBeenSubmitted, setFormHasBeenSubmitted] = useState(false);

  // Reset form state when existingDetail changes (e.g., when accordion opens)
  useEffect(() => {
    const newState = getInitialFormState();
    setFormState(newState);
    setInitialFormState(newState);
    setFormHasBeenSubmitted(false);
    setFormErrors({});
  }, [assetType]);

  // Validate form and return whether it's valid
  const validateForm = useCallback(() => {
    const errors: AccountDetailFormErrors = {};

    REQUIRED_FIELDS.forEach(field => {
      const value = formState[field as keyof AccountDetailFormState];
      if (isNullOrUndefined(value)) {
        errors[field] = 'Required Field';
      }
    });

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formState]);

  // Re-validate when form has been submitted and fields change
  useEffect(() => {
    if (formHasBeenSubmitted) {
      validateForm();
    }
  }, [
    formState.currentValue,
    formState.expectedReturn,
    formState.capitalGainsTaxRate,
    formHasBeenSubmitted,
    validateForm,
  ]);

  // Curried change handler (same pattern as SelfFundingDetailsModal)
  const handleChangeField = useCallback(
    (field: AccountDetailField) => (value: number | null) => {
      setFormState(prev => ({
        ...prev,
        [field]: value,
      }));
    },
    [],
  );

  // Submit handler - validates and saves to Redux
  const handleSubmit = useCallback(() => {
    setFormHasBeenSubmitted(true);
    const isValid = validateForm();

    if (!isValid) {
      return false;
    }

    // Save to Redux with isComplete: true
    dispatch(
      setAccountDetail({
        assetType,
        detail: {
          currentValue: formState.currentValue ?? 0,
          futureContributions: formState.futureContributions ?? 0,
          expectedReturn: formState.expectedReturn ?? 0,
          capitalGainsTaxRate: formState.capitalGainsTaxRate ?? 0,
          isComplete: true,
        },
      }),
    );

    return true;
  }, [assetType, dispatch, formState, validateForm]);

  // Reset form to initial state
  const resetForm = useCallback(() => {
    setFormState(initialFormState);
    setFormErrors({});
    setFormHasBeenSubmitted(false);
  }, [initialFormState]);

  // Check if form has unsaved changes
  const isDirty =
    formState.currentValue !== initialFormState.currentValue ||
    formState.futureContributions !== initialFormState.futureContributions ||
    formState.expectedReturn !== initialFormState.expectedReturn ||
    formState.capitalGainsTaxRate !== initialFormState.capitalGainsTaxRate;

  // Check if form is currently valid (for UI purposes)
  const isValid = Object.keys(formErrors).length === 0;

  // Check if there are required field errors
  const hasRequiredFieldErrors = REQUIRED_FIELDS.some(field => formErrors[field]);

  return {
    formState,
    formErrors,
    formHasBeenSubmitted,
    isDirty,
    isValid,
    hasRequiredFieldErrors,
    handleChangeField,
    handleSubmit,
    resetForm,
  };
}

